// src/observer/bgControl.ts
// Pause/resume background animations for a section.
// Primary path: GSAP tweens created by bgIgnition (stored on element as __bgTween).
// Secondary path: fall back to gsap.getTweensOf(element) if not stored.
//
// CSS class toggling is kept as a harmless no-op for legacy cases.

type MaybeGSAP = typeof import("gsap") | undefined;

function getGSAP(): MaybeGSAP {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require("gsap");
    return (mod && (mod.gsap || mod.default)) || mod;
  } catch {
    return (window as any).gsap as MaybeGSAP;
  }
}

function eachBgSprites(section: HTMLElement, fn: (el: HTMLElement) => void) {
  const sprites = section.querySelectorAll<HTMLElement>(".bg-anim-sprite");
  sprites.forEach(fn);
}

function pauseEl(el: HTMLElement, gsap: MaybeGSAP) {
  // Legacy CSS hooks (harmless if no CSS animations present)
  el.classList.remove("animate-bg");
  el.style.animationPlayState = "paused";

  // Prefer the explicit tween stored by bgIgnition
  const t = (el as any).__bgTween;
  if (t && typeof t.pause === "function") {
    t.pause();
    (el as any).__bgPaused = true;
    el.dataset.bgPaused = "1";
    return;
  }

  // Fallback: pause all tweens targeting this element
  if (gsap && (gsap as any).getTweensOf) {
    try {
      const tweens: any[] = (gsap as any).getTweensOf(el, false) || [];
      tweens.forEach((tw) => tw && typeof tw.pause === "function" && tw.pause());
      if (tweens.length) {
        (el as any).__bgPaused = true;
        el.dataset.bgPaused = "1";
      }
    } catch {}
  }
}

function playEl(el: HTMLElement, gsap: MaybeGSAP) {
  // Only resume if we had paused it earlier.
  if (el.dataset.bgPaused !== "1" && !(el as any).__bgPaused) return;

  // Legacy CSS hooks (harmless if no CSS animations present)
  el.classList.add("animate-bg");
  el.style.animationPlayState = "running";

  // Prefer the explicit tween stored by bgIgnition
  const t = (el as any).__bgTween;
  if (t && typeof t.play === "function") {
    t.play();
    delete (el as any).__bgPaused;
    delete el.dataset.bgPaused;
    return;
  }

  // Fallback: play all tweens targeting this element
  if (gsap && (gsap as any).getTweensOf) {
    try {
      const tweens: any[] = (gsap as any).getTweensOf(el, false) || [];
      tweens.forEach((tw) => tw && typeof tw.play === "function" && tw.play());
      if (tweens.length) {
        delete (el as any).__bgPaused;
        delete el.dataset.bgPaused;
      }
    } catch {}
  }
}

export function pauseBackgroundFor(section: HTMLElement) {
  const gsap = getGSAP();
  eachBgSprites(section, (el) => pauseEl(el, gsap));
}

export function resumeBackgroundFor(section: HTMLElement) {
  const gsap = getGSAP();
  eachBgSprites(section, (el) => playEl(el, gsap));
}